/**
 * TruePrice Canvas Navigation — SwipeablePageContainer
 * 
 * Uses native touch events for reliable bidirectional swipe.
 * Controlled component - parent manages currentPage.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useReducedMotion } from '../../hooks/useReducedMotion';

export interface CanvasPage {
  id: string;
  component: React.ReactNode;
  label: string;
}

interface SwipeablePageContainerProps {
  pages: CanvasPage[];
  currentPage: number;
  onPageChange: (index: number) => void;
  className?: string;
}

export function SwipeablePageContainer({
  pages,
  currentPage,
  onPageChange,
  className = '',
}: SwipeablePageContainerProps) {
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startX = useRef(0);
  const startY = useRef(0);
  const startTime = useRef(0);
  const isHorizontalSwipe = useRef<boolean | null>(null);
  const prefersReducedMotion = useReducedMotion();

  // Reset drag state when page changes externally
  useEffect(() => {
    setDragOffset(0);
    setIsDragging(false);
  }, [currentPage]);

  const isSwipeableRow = useRef(false);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (prefersReducedMotion) return;

    // Skip page swipe if touch started in a swipeable row
    const target = e.target as HTMLElement;
    isSwipeableRow.current = !!target.closest?.('[data-swipeable-row]');

    const touch = e.touches[0];
    startX.current = touch.clientX;
    startY.current = touch.clientY;
    startTime.current = Date.now();
    isHorizontalSwipe.current = null;
    setIsDragging(!isSwipeableRow.current);
  }, [prefersReducedMotion]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging || prefersReducedMotion || isSwipeableRow.current) return;
    
    const touch = e.touches[0];
    const diffX = touch.clientX - startX.current;
    const diffY = touch.clientY - startY.current;
    
    // Determine swipe direction on first significant move
    if (isHorizontalSwipe.current === null) {
      if (Math.abs(diffX) > 10 || Math.abs(diffY) > 10) {
        isHorizontalSwipe.current = Math.abs(diffX) > Math.abs(diffY);
      }
    }
    
    // Only handle horizontal swipes
    if (isHorizontalSwipe.current !== true) return;
    
    const containerWidth = containerRef.current?.offsetWidth || window.innerWidth;
    let percent = (diffX / containerWidth) * 100;
    
    // Rubber band at edges
    const isAtStart = currentPage === 0 && percent > 0;
    const isAtEnd = currentPage === pages.length - 1 && percent < 0;
    
    if (isAtStart || isAtEnd) {
      percent *= 0.3;
    }
    
    setDragOffset(percent);
  }, [isDragging, currentPage, pages.length, prefersReducedMotion]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!isDragging) return;
    
    const touch = e.changedTouches[0];
    const diffX = touch.clientX - startX.current;
    const containerWidth = containerRef.current?.offsetWidth || window.innerWidth;
    const percent = (diffX / containerWidth) * 100;
    const elapsed = Date.now() - startTime.current;
    const velocity = Math.abs(diffX) / elapsed; // px/ms
    
    setIsDragging(false);
    setDragOffset(0);
    
    // Only change page for horizontal swipes
    if (isHorizontalSwipe.current !== true) return;
    
    let targetPage = currentPage;
    
    // Threshold: 15% of screen OR fast swipe (0.5 px/ms with some movement)
    const distanceThreshold = 15;
    const velocityThreshold = 0.5;
    const minDistance = 5;
    
    if (percent > distanceThreshold || (percent > minDistance && velocity > velocityThreshold)) {
      // Swiped right - go to previous page
      if (currentPage > 0) {
        targetPage = currentPage - 1;
      }
    } else if (percent < -distanceThreshold || (percent < -minDistance && velocity > velocityThreshold)) {
      // Swiped left - go to next page
      if (currentPage < pages.length - 1) {
        targetPage = currentPage + 1;
      }
    }
    
    if (targetPage !== currentPage) {
      onPageChange(targetPage);
    }
  }, [isDragging, currentPage, pages.length, onPageChange]);

  // Mouse events for desktop testing
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (prefersReducedMotion) return;
    const target = e.target as HTMLElement;
    isSwipeableRow.current = !!target.closest?.('[data-swipeable-row]');
    startX.current = e.clientX;
    startY.current = e.clientY;
    startTime.current = Date.now();
    isHorizontalSwipe.current = null;
    setIsDragging(!isSwipeableRow.current);
  }, [prefersReducedMotion]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging || prefersReducedMotion) return;
    
    const diffX = e.clientX - startX.current;
    const diffY = e.clientY - startY.current;
    
    if (isHorizontalSwipe.current === null) {
      if (Math.abs(diffX) > 10 || Math.abs(diffY) > 10) {
        isHorizontalSwipe.current = Math.abs(diffX) > Math.abs(diffY);
      }
    }
    
    if (isHorizontalSwipe.current !== true) return;
    
    const containerWidth = containerRef.current?.offsetWidth || window.innerWidth;
    let percent = (diffX / containerWidth) * 100;
    
    const isAtStart = currentPage === 0 && percent > 0;
    const isAtEnd = currentPage === pages.length - 1 && percent < 0;
    
    if (isAtStart || isAtEnd) {
      percent *= 0.3;
    }
    
    setDragOffset(percent);
  }, [isDragging, currentPage, pages.length, prefersReducedMotion]);

  const handleMouseUp = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    
    const diffX = e.clientX - startX.current;
    const containerWidth = containerRef.current?.offsetWidth || window.innerWidth;
    const percent = (diffX / containerWidth) * 100;
    const elapsed = Date.now() - startTime.current;
    const velocity = Math.abs(diffX) / elapsed;
    
    setIsDragging(false);
    setDragOffset(0);
    
    if (isHorizontalSwipe.current !== true) return;
    
    let targetPage = currentPage;
    
    const distanceThreshold = 15;
    const velocityThreshold = 0.5;
    const minDistance = 5;
    
    if (percent > distanceThreshold || (percent > minDistance && velocity > velocityThreshold)) {
      if (currentPage > 0) {
        targetPage = currentPage - 1;
      }
    } else if (percent < -distanceThreshold || (percent < -minDistance && velocity > velocityThreshold)) {
      if (currentPage < pages.length - 1) {
        targetPage = currentPage + 1;
      }
    }
    
    if (targetPage !== currentPage) {
      onPageChange(targetPage);
    }
  }, [isDragging, currentPage, pages.length, onPageChange]);

  const handleMouseLeave = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      setDragOffset(0);
    }
  }, [isDragging]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const activeElement = document.activeElement;
      const isInputFocused = activeElement?.tagName === 'INPUT' || 
                            activeElement?.tagName === 'TEXTAREA';
      
      if (isInputFocused) return;

      if (e.key === 'ArrowLeft' && currentPage > 0) {
        e.preventDefault();
        onPageChange(currentPage - 1);
      } else if (e.key === 'ArrowRight' && currentPage < pages.length - 1) {
        e.preventDefault();
        onPageChange(currentPage + 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, pages.length, onPageChange]);

  const transitionStyle = isDragging || prefersReducedMotion
    ? 'none'
    : 'transform 0.28s cubic-bezier(0.32, 0.72, 0, 1)';

  return (
    <div 
      ref={containerRef}
      className={`relative w-full h-full overflow-hidden ${className}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      role="tabpanel"
      aria-label={`${pages[currentPage]?.label || 'Page'} view`}
      style={{ touchAction: 'pan-y pinch-zoom' }}
    >
      {pages.map((page, index) => {
        const offset = (index - currentPage) * 100 + dragOffset;
        
        return (
          <div
            key={page.id}
            className="absolute inset-0 w-full h-full overflow-y-auto overscroll-y-contain"
            style={{
              transform: `translateX(${offset}%)`,
              transition: transitionStyle,
              willChange: isDragging ? 'transform' : 'auto',
              overflowX: 'clip',
              contain: 'inline-size',
            }}
          >
            {page.component}
          </div>
        );
      })}
    </div>
  );
}

export default SwipeablePageContainer;
